import { ContactLink } from "../lib/contact";
import { useEffect, useState } from "react";
import { ExternalLink, Receipt } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "../components/AppHeader";
import { PackGrid } from "../components/PackGrid";
import { FAIR_USE_PER_MONTH } from "../lib/pricingConfig";
import { useWallet } from "../hooks/useWallet";
import { supabase, API, authHeaders } from "../lib/supabase";

const PLAN = { active: "Solo Pro — active", past_due: "Solo Pro — payment failed", canceled: "Credits (subscription ended)", refunded_read_only: "Read-only (refunded)" };
const SUB_LABEL = { active: "Subscription started", canceled: "Subscription canceled", past_due: "Subscription payment failed", inactive: "Subscription ended" };
const label = (l) => {
  const s = l.source || "";
  if (s.startsWith("stripe:pack")) return `Purchase · ${s.replace("stripe:pack_", "").replace(/^\w/, (c) => c.toUpperCase())} pack`;
  if (s === "stripe:sub_solo") return "Subscription started";
  if (s === "stripe:refund") return `Refund · balance cleared${l.credits ? ` (${-l.credits} credits removed)` : ""}`.replace("(-", "(\u2212");
  if (s.startsWith("stripe:subscription:")) return SUB_LABEL[s.split(":").pop()] || "Subscription update";
  if (s === "dispatch") return "Dispatch";
  if (s === "dispatch_refund") return "Credit returned (draft canceled)";
  return s.replace(/[_:]/g, " ");
};
const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export default function Billing() {
  const { wallet, usage, refresh } = useWallet();
  const [ledger, setLedger] = useState(null);
  const [payments, setPayments] = useState(null);
  const [showAbandoned, setShowAbandoned] = useState(false);
  const isAbandoned = (p) => ["initiated", "pending", "unpaid"].includes(p.payment_status) && Date.now() - new Date(p.created_at).getTime() > 3600e3;
  const packName = (id) => id === "sub_solo" ? "Solo Pro" : `${id.replace("pack_", "").replace(/^\w/, (c) => c.toUpperCase())} pack`;
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalUnavailable, setPortalUnavailable] = useState(false);

  useEffect(() => {
    refresh();
    supabase.from("wallet_ledger").select("*").order("created_at", { ascending: false }).limit(50).then(({ data }) => setLedger(data || []));
    supabase.from("payment_transactions").select("*").order("created_at", { ascending: false }).limit(50).then(({ data }) => setPayments(data || []));
  }, [refresh]);

  const unlimited = wallet?.subscription_status === "active";
  const plan = PLAN[wallet?.subscription_status] || (wallet?.tier === "trial" && !(ledger || []).some((l) => l.source.startsWith("stripe:pack")) ? "Trial" : "Credits");
  const subscribed = ["active", "past_due"].includes(wallet?.subscription_status);

  const portal = async () => {
    setPortalBusy(true);
    try {
      const r = await fetch(`${API}/billing/portal`, { method: "POST", headers: { "Content-Type": "application/json", ...(await authHeaders()) }, body: JSON.stringify({ origin_url: window.location.origin }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Portal unavailable");
      window.location.href = d.portal_url;
    } catch (e) { setPortalUnavailable(true); toast.error(e.message); setPortalBusy(false); }
  };

  return (
    <main className="shell" data-testid="billing-page">
      <AppHeader back="/app" />
      <section className="card fade-up">
        <p className="eyebrow">Your plan</p>
        <h2 className="card-title" data-testid="billing-plan">{plan}</h2>
        <p className="wallet-number mt-3" data-testid="billing-balance">{unlimited ? "Unlimited" : wallet ? wallet.credit_balance : "—"}</p>
        <p className="text-sm text-muted">{unlimited ? `dispatches while your subscription is active (fair use: ${FAIR_USE_PER_MONTH} jobs/month)` : "credits available · 1 credit = 1 dispatch"}</p>
        {subscribed && usage && (
          <div className="mt-4" data-testid="fair-use">
            <p className="text-sm font-semibold" data-testid="fair-use-text">Solo Pro · {usage.used} / {usage.limit} jobs this month · resets {new Date(usage.resets_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</p>
            <div className="progress mt-2"><div className={`progress-fill ${usage.used >= usage.limit ? "bg-danger" : usage.used >= 450 ? "bg-warn" : ""}`} style={{ width: `${Math.min(100, (usage.used / usage.limit) * 100)}%` }} data-testid="fair-use-bar" /></div>
            {usage.used >= 450 && usage.used < usage.limit && <p className="banner-warn mt-3 mb-0 text-xs" data-testid="fair-use-near">You're near this month's fair-use limit ({usage.limit}). Extra jobs will use pack credits.</p>}
            {usage.used >= usage.limit && <p className="banner-danger mt-3 mb-0 text-xs" data-testid="fair-use-reached">Fair-use limit reached for this month — extra jobs use pack credits ({wallet.credit_balance} left).</p>}
          </div>
        )}
        {wallet?.subscription_status === "canceled" && <p className="text-xs text-muted mt-2" data-testid="billing-canceled-note">Your subscription ended. Credits you bought are still yours.</p>}
        {subscribed && (
          <div className="mt-4">
            {portalUnavailable ? (
              <p className="text-sm text-muted" data-testid="billing-portal-fallback">To cancel or update your card, message us via Messenger: <ContactLink testid="billing-contact-link" />.</p>
            ) : (
              <button type="button" className="btn-secondary flex items-center justify-center gap-2" onClick={portal} disabled={portalBusy} data-testid="manage-subscription-button"><ExternalLink size={16} /> {portalBusy ? "Opening…" : "Manage subscription"}</button>
            )}
          </div>
        )}
      </section>

      <section className="card mt-5 fade-up" data-testid="billing-tiers">
        <p className="eyebrow">Top up or go unlimited</p>
        <div className="mt-3"><PackGrid testidPrefix="billing-buy" /></div>
      </section>

      <section className="card mt-5 fade-up" data-testid="billing-history">
        <p className="eyebrow flex items-center gap-2"><Receipt size={14} /> Credit history</p>
        {ledger === null ? <p className="text-sm text-muted mt-3">Loading…</p> : ledger.length === 0 ? <p className="text-sm text-muted mt-3" data-testid="billing-history-empty">No activity yet.</p> : (
          <div className="mt-3 divide-y divide-[var(--line)]" data-testid="billing-ledger">
            {ledger.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-2 text-sm" data-testid={`ledger-${l.id}`}>
                <div><p className="font-semibold">{label(l)}</p><p className="text-xs text-muted">{fmt(l.created_at)}</p></div>
                <span className={`font-black ${l.credits > 0 ? "text-[#2ECC71]" : l.credits < 0 ? "text-[#FF7A7A]" : "text-muted"}`}>{l.credits > 0 ? `+${l.credits}` : l.credits === 0 ? "—" : `\u2212${-l.credits}`}</span>
              </div>
            ))}
          </div>
        )}
        {payments && payments.length > 0 && (
          <div className="mt-5" data-testid="billing-payments">
            <p className="eyebrow">Payments</p>
            {payments.filter((p) => showAbandoned || !isAbandoned(p)).map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2 text-sm" data-testid={`payment-${p.id}`}>
                <div><p className="font-semibold">{packName(p.pack_id)} · {isAbandoned(p) ? "abandoned" : p.payment_status}</p><p className="text-xs text-muted">{fmt(p.created_at)}</p></div>
                <span className="font-mono">${Number(p.amount).toFixed(2)} {p.currency.toUpperCase()}</span>
              </div>
            ))}
            {payments.some(isAbandoned) && (
              <button type="button" className="text-xs text-muted underline mt-1" onClick={() => setShowAbandoned((v) => !v)} data-testid="toggle-abandoned">
                {showAbandoned ? "Hide abandoned" : `Show abandoned (${payments.filter(isAbandoned).length})`}
              </button>
            )}
          </div>
        )}
      </section>
    </main>
  );
}
